import React, {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { AuthContext } from '../../contexts/AuthContext';
import useInterviewFeedback from '../../hooks/useInterviewFeedback';
import InterviewFeedbackModal from './InterviewFeedbackModal';

/**
 * Mounts the feedback prompt app-wide, for whichever side is signed in.
 *
 * Both a provider and a recruiter are asked about the same interview, and
 * completion needs BOTH answers (with the provider's deciding a conflict), so
 * this cannot live on one side's layout only. It sits in the app-wide Layout
 * instead, and renders nothing at all without a session: the endpoint is
 * authenticated, and an anonymous visitor asking for pending feedback is a
 * guaranteed 401 on every page load.
 *
 * One interview at a time. A queue of modals is a wall, and the next one is
 * still there on the next load.
 */

/**
 * The forced pipeline-review queue. This prompt is mounted in the app-wide
 * Layout, which sits OUTSIDE PipelineReviewGate, so without this suppression a
 * dismissable "did this interview happen?" modal would pop over the
 * non-dismissable disposition queue — two modals asking overlapping questions
 * about the same negotiation, one of which can be waved away. The blocking
 * queue wins while it is on screen; this prompt returns on the next page the
 * user reaches, unchanged.
 */
const REVIEW_QUEUE_PATH = '/company/pipeline/review';
const MAGIC_FEEDBACK_PATH = '/interview-feedback/';

export default function InterviewFeedbackPrompt() {
  const { auth } = useContext(AuthContext) ?? {};
  const { pathname } = useLocation();
  const signedIn = !!auth?.token;
  const onReviewQueue = pathname.startsWith(REVIEW_QUEUE_PATH);
  const onMagicFeedback = pathname.startsWith(MAGIC_FEEDBACK_PATH);

  // Checked here rather than inside SignedInPrompt so the hook's fetch never
  // fires on the review page either.
  return signedIn && !onReviewQueue && !onMagicFeedback ? <SignedInPrompt /> : null;
}
/**
 * What to say when a progression answer saved but the thing it promised did
 * not happen. The modal states the consequence BEFORE the click ("Moves this
 * candidate to credentialing", "This closes the negotiation"), and the backend
 * deliberately swallows a transition the negotiation refuses so a race cannot
 * cost us the attestation. Both are right; the silence between them was not.
 *
 * Named per intent because the broken promise differs: a recruiter needs to
 * know credentialing did NOT start (there is a candidate sitting in a stage
 * they think is underway), and a provider needs to know the negotiation was
 * already closed rather than closed by them just now. The generic line covers
 * any intent that grows a transition later, so a new one degrades to something
 * true rather than to silence.
 */
const NOTICE_BY_INTENT = {
  advancing:
    'Your answer was saved. Credentialing could not be started, because this negotiation has already moved on.',
  withdrawn:
    'Your answer was saved. This negotiation had already closed, so nothing further changed.',
};
const GENERIC_NOTICE =
  'Your answer was saved. The next step could not be applied, because this negotiation has already moved on.';

/**
 * Split out so the hook (and its fetch) never runs for a signed-out visitor.
 * Hooks cannot be called conditionally, so the condition has to be a boundary
 * between two components rather than a branch inside one.
 */
function SignedInPrompt() {
  const { pending, submit, isLoading, refresh } = useInterviewFeedback();
  const location = useLocation();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  // The notice, and the interview it belongs to. Both are needed because a
  // successful submit REMOVES the interview from `pending` (that is how the
  // modal normally disappears), so by the time there is anything to say, the
  // row it is about is already gone from the list below.
  const [notice, setNotice] = useState(null);
  const [noticeItem, setNoticeItem] = useState(null);
  const [dismissed, setDismissed] = useState(() => new Set());
  // The ?confirmInterview deep link: the journey board's "Confirm your
  // interview" CTA lands here, because the pipeline page it navigates to has
  // no confirm control of its own. `explicitAsk` remembers that the user
  // ASKED to confirm, which earns them an honest empty state when there is
  // nothing to confirm; an ordinary page load stays silent as ever.
  // `checking` covers the refresh in flight, so the empty state cannot flash
  // while a stale pending list is being replaced.
  const [explicitAsk, setExplicitAsk] = useState(false);
  const [checking, setChecking] = useState(false);
  const autoOpenedSwipe = useRef(null);

  const wantsConfirm = useMemo(
    () => new URLSearchParams(location.search || '').has('confirmInterview'),
    [location.search],
  );

  useEffect(() => {
    if (!wantsConfirm) return;
    // Forgive this-session dismissals: following the CTA is the user asking
    // for the question back.
    setDismissed(new Set());
    setExplicitAsk(true);
    setChecking(true);
    // Drop the param without a history entry, same as CompanyLayout does for
    // ?interest=. Left in place it would re-summon the prompt on every
    // back/forward through this URL.
    const params = new URLSearchParams(location.search || '');
    params.delete('confirmInterview');
    const search = params.toString();
    navigate(
      { pathname: location.pathname, search: search ? `?${search}` : '' },
      { replace: true },
    );
    // The pending list was fetched once, on Layout mount, which may be many
    // navigations ago. An explicit ask deserves a fresh answer.
    refresh().finally(() => setChecking(false));
  }, [wantsConfirm, location.pathname, location.search, navigate, refresh]);

  const next = (pending ?? []).find(
    (row) => !dismissed.has(row.scheduledInterviewId),
  );

  // A due, unanswered interview owns the next screen after login. Open the
  // exact negotiation once per page lifetime, then let the modal ask for the
  // update in context. The guard prevents refreshes from fighting navigation.
  useEffect(() => {
    if (!next?.swipeId || notice) return;
    if (autoOpenedSwipe.current === next.swipeId) return;
    const target = `/negotiations/${encodeURIComponent(next.swipeId)}`;
    autoOpenedSwipe.current = next.swipeId;
    if (location.pathname !== target) navigate(target);
  }, [next?.swipeId, notice, location.pathname, navigate]);
  // A notice holds the modal on the interview it is about. Without this the
  // component would fall straight through to the next pending interview, or
  // to null, and the notice would flash out of existence in the same tick it
  // was set.
  const item = notice ? noticeItem : next;

  // The ask is answered the moment the modal shows. Clearing the flag here is
  // what keeps a later "Not now" from bouncing the user into the empty state:
  // they saw the question and declined it, which is not "nothing to confirm".
  useEffect(() => {
    if (explicitAsk && !checking && !isLoading && item) setExplicitAsk(false);
  }, [explicitAsk, checking, isLoading, item]);

  if (isLoading || !item) {
    return explicitAsk && !checking && !isLoading ? (
      <NothingToConfirmNotice onClose={() => setExplicitAsk(false)} />
    ) : null;
  }

  const handleSubmit = async (payload) => {
    setBusy(true);
    setError(null);
    try {
      const result = await submit(payload);
      // transitionAttempted separates "this answer never moved anything and
      // never claimed to" from "we promised to move the deal and could not".
      // Only the second is something the respondent has to be told, and only
      // the pair can tell them apart: transitionFired is null in both cases.
      const outcome = result?.disposition;
      if (
        outcome?.transitionAttempted === true &&
        outcome.transitionFired == null
      ) {
        setNoticeItem(item);
        setNotice(NOTICE_BY_INTENT[payload.disposition] ?? GENERIC_NOTICE);
      }
    } catch {
      // An answer that did not save must not silently vanish -- and must not
      // read as a click that did nothing, which is what leaving the modal
      // open in silence looked like. The modal stays open AND says why.
      setError(
        'We could not save that answer. Check your connection and try again.',
      );
    } finally {
      setBusy(false);
    }
  };

  const handleClose = () => {
    setError(null);
    setNotice(null);
    setNoticeItem(null);
    // Dismissed for this page load only. The prompt returns on the next one,
    // because the interview is still unreported.
    //
    // Dismissing on the notice path too, not just the "Not now" one: that
    // interview HAS been answered, and the write is immutable. If the refresh
    // that follows a submit has not landed (or failed), the row is briefly
    // still in `pending`, and clearing the notice without this would re-ask a
    // question the user just answered and cannot answer again.
    setDismissed((prev) => new Set(prev).add(item.scheduledInterviewId));
  };

  return (
    <InterviewFeedbackModal
      // KEYED ON THE INTERVIEW, WHICH IS LOAD-BEARING. The modal holds the
      // chosen outcome and the notes in local state, and this component swaps
      // `item` underneath the same instance as each interview is answered or
      // dismissed. Without the key, a selection (or a note) made about one
      // interview would still be sitting there when the next one appears, and
      // the answer is immutable once submitted.
      key={item.scheduledInterviewId}
      item={item}
      busy={busy}
      error={error}
      notice={notice}
      onSubmit={handleSubmit}
      onClose={handleClose}
    />
  );
}

/**
 * What an explicit ask gets when the pending list comes back empty: either
 * every due interview already has this side's answer (completion is then the
 * provider's confirmation, out of this user's hands), or no interview has
 * passed its slot yet. Silence here would be the same dead end the deep link
 * exists to fix: the user clicked a button that said "Confirm your interview"
 * and must not land on a page that says nothing at all.
 *
 * Mirrors the modal's notice mode (overlay, dialog, one Close), minus the
 * amber: nothing went wrong.
 */
function NothingToConfirmNotice({ onClose }) {
  const boxRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    const t = setTimeout(() => boxRef.current?.focus(), 0);
    return () => clearTimeout(t);
  }, []);

  return createPortal(
    <div
      className="cv-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="if-nothing"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div
        className="cv-modal"
        ref={boxRef}
        tabIndex={-1}
        style={{ outline: 'none' }}
      >
        <p
          id="if-nothing"
          role="status"
          className="cv-text-sm"
          style={{ lineHeight: 1.5, margin: '0 0 16px' }}
        >
          Nothing needs your confirmation right now. When an interview needs
          your answer, we'll ask you here.
        </p>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button
            type="button"
            className="cv-btn cv-btn--primary"
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
